/**
 * Store release console (migration 0472, apps/worker routes/admin-store.ts).
 *
 * Opens and closes the Postopia Store country by country, and keeps the
 * tester list (accounts that see the store where their country is still
 * closed). Every change lands in the dated log below. The master switch,
 * `store:enabled`, is shown read-only: it is flipped deliberately with
 * wrangler, like `contests:enabled`, and nobody sees the store while it is
 * off, testers included.
 *
 * Opening a country uses "click again to confirm" (no window.confirm, §0c).
 */

import { PageShell } from '../components/ui/PageShell';
import { parseServerDate } from '@postopia/shared';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import { AdminShell } from '../components/AdminShell';
import { storeApi, StoreAdminError, type StoreRelease } from '../lib/storeApi';

export const Route = createFileRoute('/store')({
  component: StoreRoute,
});

function StoreRoute() {
  return (
    <AdminShell mode="feature" featureName="Store release">
      <StoreConsole />
    </AdminShell>
  );
}

function when(iso: string): string {
  const d = parseServerDate(iso);
  return Number.isFinite(d.getTime()) ? d.toLocaleString() : '';
}

const input: React.CSSProperties = {
  padding: '8px 12px',
  borderRadius: 8,
  border: '1px solid var(--c-border-hairline)',
  background: 'var(--c-bg-page)',
  color: 'var(--c-fg-default)',
  fontSize: 14,
};

const primary: React.CSSProperties = {
  padding: '8px 18px',
  borderRadius: 8,
  border: 0,
  background: 'var(--c-fg-default)',
  color: 'var(--c-bg-page)',
  fontWeight: 600,
  fontSize: 14,
  cursor: 'pointer',
};

const quiet: React.CSSProperties = {
  padding: '7px 14px',
  borderRadius: 8,
  border: '1px solid var(--c-border-hairline)',
  background: 'transparent',
  color: 'var(--c-fg-default)',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};

const card: React.CSSProperties = {
  background: 'var(--c-bg-surface)',
  border: '1px solid var(--c-border-hairline)',
  borderRadius: 10,
  padding: 14,
};

const h2: React.CSSProperties = { fontSize: 16, fontWeight: 600, margin: '24px 0 10px', color: 'var(--c-fg-default)' };

function StoreConsole() {
  const [data, setData] = useState<StoreRelease | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [arming, setArming] = useState<string | null>(null);
  const [newCountry, setNewCountry] = useState('');
  const [countryNote, setCountryNote] = useState('');
  const [tester, setTester] = useState('');
  const [testerNote, setTesterNote] = useState('');

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await storeApi.get());
    } catch (err) {
      if (err instanceof StoreAdminError && err.status === 403) setError('Your account does not have admin access.');
      else if (err instanceof StoreAdminError && err.code === 'store_admin_not_configured')
        setError('Store release is not configured on this environment yet.');
      else setError("Couldn't load the store release state. Try again.");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const setCountry = useCallback(
    async (country: string, enabled: boolean, note: string) => {
      const key = `${country}:${enabled}`;
      // Opening is the consequential direction: confirm it with a second click.
      if (enabled && arming !== key) {
        setArming(key);
        return;
      }
      setArming(null);
      setBusy(key);
      setStatus(null);
      try {
        await storeApi.setCountry(country, enabled, note || undefined);
        setStatus(enabled ? `${country} is open. People there see the store within about 30 seconds.` : `${country} is closed.`);
        setNewCountry('');
        setCountryNote('');
        await load();
      } catch (err) {
        setStatus(err instanceof StoreAdminError && err.code === 'invalid_country' ? 'Use a two-letter country code, like GH or US.' : 'That change failed. Try again.');
      } finally {
        setBusy(null);
      }
    },
    [arming, load],
  );

  const addTester = useCallback(async () => {
    const who = tester.trim();
    if (!who) return;
    setBusy('tester:add');
    setStatus(null);
    try {
      const r = await storeApi.addTester(who, testerNote || undefined);
      setStatus(`@${r.handle} is a store tester.`);
      setTester('');
      setTesterNote('');
      await load();
    } catch (err) {
      setStatus(err instanceof StoreAdminError && err.code === 'user_not_found' ? 'No account with that handle or id.' : 'Adding the tester failed. Try again.');
    } finally {
      setBusy(null);
    }
  }, [tester, testerNote, load]);

  const removeTester = useCallback(
    async (userId: string, handle: string) => {
      setBusy(`tester:${userId}`);
      setStatus(null);
      try {
        await storeApi.removeTester(userId);
        setStatus(`@${handle} is no longer a store tester.`);
        await load();
      } catch {
        setStatus('Removing the tester failed. Try again.');
      } finally {
        setBusy(null);
      }
    },
    [load],
  );

  const draftCode = newCountry.trim().toUpperCase();

  return (
    <PageShell
      title="Store release"
      description={
        <>
          The Postopia Store opens country by country. Someone sees it only when the store is on, their country on
          Postopia is open, or they are on the tester list. A purchase also needs the shipping country open.
        </>
      }
    >
      {status ? <p style={{ fontSize: 13, color: 'var(--c-fg-secondary)', margin: '0 0 12px' }}>{status}</p> : null}
      {error ? <p style={{ fontSize: 14, color: '#9A2A12', margin: '0 0 12px' }}>{error}</p> : null}

      {data === null ? (
        <p style={{ color: 'var(--c-fg-tertiary)', fontSize: 14 }}>{error ? '' : 'Loading…'}</p>
      ) : (
        <>
          <div style={{ ...card, display: 'flex', gap: 12, alignItems: 'center' }}>
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 0.3,
                padding: '2px 10px',
                borderRadius: 999,
                background: data.store_enabled ? '#E3EBD9' : 'var(--c-bg-surface-alt)',
                color: data.store_enabled ? '#3F5429' : 'var(--c-fg-secondary)',
              }}
            >
              {data.store_enabled ? 'STORE ON' : 'STORE OFF'}
            </span>
            <span style={{ fontSize: 13, color: 'var(--c-fg-secondary)' }}>
              {data.store_enabled
                ? 'Open countries and testers see the store.'
                : 'Nobody sees the store, testers included. Countries below take effect when it is turned on.'}{' '}
              The switch is the KV key <code>store:enabled</code>.
            </span>
          </div>

          <h2 style={h2}>Countries</h2>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (/^[A-Za-z]{2}$/.test(newCountry.trim())) void setCountry(draftCode, true, countryNote);
              else setStatus('Use a two-letter country code, like GH or US.');
            }}
            style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}
          >
            <input
              value={newCountry}
              onChange={(e) => {
                setNewCountry(e.target.value);
                setArming(null);
              }}
              maxLength={2}
              placeholder="GH"
              aria-label="Country code"
              style={{ ...input, width: 70, textTransform: 'uppercase' }}
            />
            <input
              value={countryNote}
              onChange={(e) => setCountryNote(e.target.value)}
              placeholder="Note, for the log (optional)"
              aria-label="Note"
              style={{ ...input, flex: 1, minWidth: 200 }}
            />
            <button type="submit" style={{ ...primary, background: arming === `${draftCode}:true` ? '#3F5429' : primary.background }}>
              {arming === `${draftCode}:true` ? `Confirm: open ${draftCode}` : 'Open country'}
            </button>
          </form>
          {data.countries.length === 0 ? (
            <p style={{ color: 'var(--c-fg-tertiary)', fontSize: 14 }}>No country is open. Every country is closed until it has a row here.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {data.countries.map((r) => {
                const key = `${r.country}:${!r.enabled}`;
                return (
                  <div key={r.country} style={{ ...card, display: 'flex', gap: 12, alignItems: 'center' }}>
                    <strong style={{ fontSize: 16, width: 36 }}>{r.country}</strong>
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        padding: '1px 8px',
                        borderRadius: 999,
                        background: r.enabled ? '#E3EBD9' : 'var(--c-bg-surface-alt)',
                        color: r.enabled ? '#3F5429' : 'var(--c-fg-secondary)',
                      }}
                    >
                      {r.enabled ? 'Open' : 'Closed'}
                    </span>
                    <span style={{ flex: 1, minWidth: 0, fontSize: 12, color: 'var(--c-fg-tertiary)' }}>
                      {r.changed_by} · {when(r.changed_at)}
                      {r.note ? ` · ${r.note}` : ''}
                    </span>
                    <button
                      type="button"
                      disabled={busy === key}
                      onClick={() => void setCountry(r.country, !r.enabled, '')}
                      onBlur={() => arming === key && setArming(null)}
                      style={{ ...quiet, opacity: busy === key ? 0.5 : 1 }}
                    >
                      {busy === key ? '…' : r.enabled ? 'Close' : arming === key ? `Confirm: open ${r.country}` : 'Open'}
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          <h2 style={h2}>Testers</h2>
          <p style={{ fontSize: 13, color: 'var(--c-fg-secondary)', margin: '0 0 10px' }}>
            Testers see the store and can buy where their country is still closed, so a country can be tried before it
            opens. They never see it while the store is off.
          </p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void addTester();
            }}
            style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}
          >
            <input value={tester} onChange={(e) => setTester(e.target.value)} placeholder="@handle or user id" aria-label="Tester" style={{ ...input, minWidth: 200 }} />
            <input value={testerNote} onChange={(e) => setTesterNote(e.target.value)} placeholder="Note (optional)" aria-label="Tester note" style={{ ...input, flex: 1, minWidth: 180 }} />
            <button type="submit" disabled={busy === 'tester:add'} style={{ ...primary, opacity: busy === 'tester:add' ? 0.5 : 1 }}>
              Add tester
            </button>
          </form>
          {data.testers.length === 0 ? (
            <p style={{ color: 'var(--c-fg-tertiary)', fontSize: 14 }}>No testers.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {data.testers.map((t) => (
                <div key={t.user_id} style={{ ...card, display: 'flex', gap: 12, alignItems: 'center' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <strong style={{ fontSize: 14 }}>@{t.handle}</strong>{' '}
                    <span style={{ fontSize: 12, color: 'var(--c-fg-tertiary)' }}>
                      {t.display_name ?? ''} · {t.country ?? 'no country'} · added by {t.added_by} {when(t.added_at)}
                      {t.note ? ` · ${t.note}` : ''}
                    </span>
                  </div>
                  <button
                    type="button"
                    disabled={busy === `tester:${t.user_id}`}
                    onClick={() => void removeTester(t.user_id, t.handle)}
                    style={{ ...quiet, opacity: busy === `tester:${t.user_id}` ? 0.5 : 1 }}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}

          <h2 style={h2}>Change log</h2>
          {data.log.length === 0 ? (
            <p style={{ color: 'var(--c-fg-tertiary)', fontSize: 14 }}>No changes yet.</p>
          ) : (
            <div style={{ ...card, padding: 0 }}>
              {data.log.map((l, i) => (
                <div
                  key={l.id}
                  style={{
                    display: 'flex',
                    gap: 12,
                    padding: '10px 14px',
                    fontSize: 13,
                    borderTop: i === 0 ? 0 : '1px solid var(--c-border-hairline)',
                  }}
                >
                  <span style={{ width: 170, flexShrink: 0, color: 'var(--c-fg-tertiary)' }}>{when(l.created_at)}</span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    {l.kind === 'country'
                      ? `${l.enabled ? 'Opened' : 'Closed'} ${l.country}`
                      : `${l.enabled ? 'Added' : 'Removed'} tester @${l.handle ?? l.user_id}`}
                    <span style={{ color: 'var(--c-fg-tertiary)' }}>
                      {' '}
                      · {l.actor}
                      {l.note ? ` · ${l.note}` : ''}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </PageShell>
  );
}
